import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText, Search, X, Calendar, CheckCircle2,
  Clock, XCircle, AlertCircle, ChevronRight, DollarSign,
  Plus, Trash2, Send,
} from 'lucide-react';
import { invoicesApi, clientsApi } from '../../api/client';
import { Invoice, InvoiceStatus, Client } from '../../types';
import { InvoicePdfActions } from '../../components/invoices/InvoicePdfActions';

// ─── Design tokens ────────────────────────────────────────────────────────────

const C = {
  bg: '#0a0a0f',
  card: '#12121a',
  border: '#1e1e2e',
  borderHover: '#2e2e42',
  textPrimary: '#e2e1f0',
  textSecondary: '#6b6880',
  textMuted: '#4a4858',
  orange: '#f97316',
  orangeHover: '#ea580c',
  success: '#22c55e',
  error: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatEuro(n: number) {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n);
}

const STATUS_CONFIG: Record<InvoiceStatus, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  draft:     { label: 'Borrador',  color: C.textSecondary, bg: 'rgba(107,104,128,0.12)', icon: <FileText   size={11} /> },
  sent:      { label: 'Enviada',   color: C.info,          bg: 'rgba(59,130,246,0.12)',  icon: <Clock       size={11} /> },
  paid:      { label: 'Pagada',    color: C.success,       bg: 'rgba(34,197,94,0.12)',   icon: <CheckCircle2 size={11} /> },
  cancelled: { label: 'Cancelada', color: C.error,         bg: 'rgba(239,68,68,0.12)',   icon: <XCircle     size={11} /> },
};

const STATUS_FILTERS: { value: '' | InvoiceStatus; label: string }[] = [
  { value: '',           label: 'Todas' },
  { value: 'draft',      label: 'Borrador' },
  { value: 'sent',       label: 'Enviadas' },
  { value: 'paid',       label: 'Pagadas' },
  { value: 'cancelled',  label: 'Canceladas' },
];

function StatusBadge({ status }: { status: InvoiceStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 20,
      background: cfg.bg, color: cfg.color,
      fontSize: 11, fontWeight: 600,
      fontFamily: 'DM Sans, sans-serif',
    }}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

// ─── New Invoice Modal ────────────────────────────────────────────────────────

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  tax: number;
}

function emptyLine(): LineItem {
  return { description: '', quantity: 1, unitPrice: 0, tax: 21 };
}

function NewInvoiceModal({
  onClose,
  onSuccess,
  isMobile,
}: {
  onClose: () => void;
  onSuccess: (number: string) => void;
  isMobile: boolean;
}) {
  const queryClient = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);

  const [clientId, setClientId] = useState<number | ''>('');
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClientData, setSelectedClientData] = useState<Client | null>(null);
  const [loadingClientData, setLoadingClientData] = useState(false);
  const [issueDate, setIssueDate] = useState(today);
  const [dueDate, setDueDate] = useState('');
  const [lines, setLines] = useState<LineItem[]>([emptyLine()]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [pendingPayload, setPendingPayload] = useState<any>(null);

  const { data: rawClients } = useQuery({
    queryKey: ['clients', clientSearch],
    queryFn: () => clientsApi.list(clientSearch || undefined),
    retry: false,
  });

  const clients: Client[] = Array.isArray(rawClients) ? rawClients : [];

  // Autocomplete: fetch full client data when client is selected
  const handleSelectClient = async (c: Client) => {
    setClientId(c.id);
    setClientSearch(`${c.name}${c.company ? ` — ${c.company}` : ''}`);
    setLoadingClientData(true);
    try {
      const result = await clientsApi.get(c.id);
      const fullClient: Client = result?.data ?? result;
      setSelectedClientData(fullClient);
    } catch {
      setSelectedClientData(c);
    } finally {
      setLoadingClientData(false);
    }
  };

  const handleClearClient = () => {
    setClientId('');
    setClientSearch('');
    setSelectedClientData(null);
  };

  const mutation = useMutation({
    mutationFn: (data: any) => invoicesApi.create(data),
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setPendingPayload(null);
      onSuccess(result?.number ?? '');
    },
    onError: (_err: any, variables: any) => {
      // ERP-52: el servicio ya intentó 1 reintento automático ante race condition.
      // Si aun así falla, guardamos el payload para permitir reintentar manualmente.
      setPendingPayload(variables);
      setError('Error al crear la factura. Por favor, inténtalo de nuevo.');
    },
  });

  // Totals
  const lineSubtotals = lines.map(l => l.quantity * l.unitPrice);
  const subtotal = lineSubtotals.reduce((s, v) => s + v, 0);
  const vatAmount = lines.reduce((s, l, i) => s + lineSubtotals[i] * (l.tax / 100), 0);
  const total = subtotal + vatAmount;

  function updateLine(idx: number, field: keyof LineItem, value: string | number) {
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  }

  function addLine() {
    setLines(prev => [...prev, emptyLine()]);
  }

  function removeLine(idx: number) {
    setLines(prev => prev.filter((_, i) => i !== idx));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setPendingPayload(null);

    if (!clientId) { setError('Selecciona un cliente.'); return; }
    if (!issueDate) { setError('La fecha de emisión es obligatoria.'); return; }
    if (lines.length === 0) { setError('Añade al menos una línea de factura.'); return; }
    if (lines.some(l => !l.description.trim())) { setError('Todas las líneas deben tener descripción.'); return; }

    const payload = {
      clientId: clientId as number,
      issueDate: new Date(issueDate).toISOString(),
      dueDate: dueDate ? new Date(dueDate).toISOString() : null,
      notes: notes.trim() || null,
      items: lines.map(l => ({
        description: l.description,
        quantity: Number(l.quantity),
        unitPrice: Number(l.unitPrice),
        tax: Number(l.tax),
      })),
    };

    mutation.mutate(payload);
  }

  const inputStyle: React.CSSProperties = {
    background: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    color: C.textPrimary,
    fontSize: 13,
    padding: '8px 12px',
    outline: 'none',
    fontFamily: 'DM Sans, sans-serif',
    width: '100%',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 10,
    color: C.textMuted,
    fontFamily: 'JetBrains Mono, monospace',
    letterSpacing: '0.06em',
    display: 'block',
    marginBottom: 6,
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, backdropFilter: 'blur(2px)' }}
      />
      <div style={{
        position: 'fixed',
        top: isMobile ? 0 : '50%',
        left: isMobile ? 0 : '50%',
        transform: isMobile ? 'none' : 'translate(-50%, -50%)',
        zIndex: 201,
        width: isMobile ? '100%' : '94%',
        maxWidth: isMobile ? 'none' : 720,
        height: isMobile ? '100%' : 'auto',
        maxHeight: isMobile ? '100%' : '90vh',
        overflowY: 'auto',
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: isMobile ? 0 : 16,
        boxShadow: '0 24px 72px rgba(0,0,0,0.7)',
      }}>

        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 10,
            background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <FileText size={18} color={C.orange} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 18, color: '#fff', margin: 0, letterSpacing: '-0.02em' }}>
              Nueva Factura
            </h2>
            <p style={{ fontSize: 12, color: C.textSecondary, margin: 0 }}>
              El número se asignará automáticamente (FAC-YYYY-NNN)
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textSecondary }}
          >
            <X size={15} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Client */}
            <div>
              <label style={labelStyle}>CLIENTE *</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Buscar cliente por nombre o empresa..."
                  value={clientSearch}
                  onChange={e => { setClientSearch(e.target.value); setClientId(''); setSelectedClientData(null); }}
                  style={{ ...inputStyle, flex: '1 1 200px' }}
                />
              </div>
              {clients.length > 0 && !clientId && (
                <div style={{
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  marginTop: 4,
                  maxHeight: 180,
                  overflowY: 'auto',
                }}>
                  {clients.map((c: Client) => (
                    <div
                      key={c.id}
                      onClick={() => handleSelectClient(c)}
                      style={{
                        padding: '9px 12px',
                        cursor: 'pointer',
                        borderBottom: `1px solid rgba(30,30,46,0.5)`,
                        fontSize: 13,
                        color: C.textPrimary,
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(249,115,22,0.07)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <span style={{ fontWeight: 600 }}>{c.name}</span>
                      {c.company && <span style={{ color: C.textSecondary, marginLeft: 6 }}>{c.company}</span>}
                      {c.nif && <span style={{ color: C.textMuted, marginLeft: 6, fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }}>{c.nif}</span>}
                    </div>
                  ))}
                </div>
              )}
              {clientId && (
                <div style={{ marginTop: 8 }}>
                  {loadingClientData ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'rgba(249,115,22,0.05)', border: `1px solid rgba(249,115,22,0.15)`, borderRadius: 10 }}>
                      <div style={{ width: 14, height: 14, border: `2px solid rgba(249,115,22,0.3)`, borderTopColor: C.orange, borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: C.textSecondary }}>Cargando datos fiscales...</span>
                    </div>
                  ) : selectedClientData ? (
                    <div style={{ background: 'rgba(249,115,22,0.05)', border: `1px solid rgba(249,115,22,0.2)`, borderRadius: 10, padding: '12px 14px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <CheckCircle2 size={13} color={C.success} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: C.success }}>Datos fiscales autocargados</span>
                        </div>
                        <button
                          type="button"
                          onClick={handleClearClient}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, fontSize: 11, padding: 0 }}
                        >
                          cambiar
                        </button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '6px 16px' }}>
                        <div>
                          <span style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em' }}>NOMBRE</span>
                          <div style={{ fontSize: 12, color: C.textPrimary, marginTop: 2 }}>{selectedClientData.name}</div>
                        </div>
                        {selectedClientData.company && (
                          <div>
                            <span style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em' }}>EMPRESA</span>
                            <div style={{ fontSize: 12, color: C.textPrimary, marginTop: 2 }}>{selectedClientData.company}</div>
                          </div>
                        )}
                        {selectedClientData.nif && (
                          <div>
                            <span style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em' }}>NIF/CIF</span>
                            <div style={{ fontSize: 12, color: C.orange, fontFamily: 'JetBrains Mono, monospace', marginTop: 2 }}>{selectedClientData.nif}</div>
                          </div>
                        )}
                        {selectedClientData.address && (
                          <div>
                            <span style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em' }}>DIRECCIÓN</span>
                            <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 2 }}>{selectedClientData.address}</div>
                          </div>
                        )}
                        {(selectedClientData.postalCode || selectedClientData.city) && (
                          <div>
                            <span style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em' }}>LOCALIDAD</span>
                            <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 2 }}>
                              {[selectedClientData.postalCode, selectedClientData.city].filter(Boolean).join(' ')}
                            </div>
                          </div>
                        )}
                        {selectedClientData.email && (
                          <div>
                            <span style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em' }}>EMAIL</span>
                            <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 2 }}>{selectedClientData.email}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                      <CheckCircle2 size={13} color={C.success} />
                      <span style={{ fontSize: 12, color: C.success }}>Cliente seleccionado</span>
                      <button
                        type="button"
                        onClick={handleClearClient}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, fontSize: 11, padding: 0, marginLeft: 4 }}
                      >
                        cambiar
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Dates */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>FECHA EMISIÓN *</label>
                <input
                  type="date"
                  value={issueDate}
                  onChange={e => setIssueDate(e.target.value)}
                  required
                  style={{ ...inputStyle, colorScheme: 'dark' }}
                />
              </div>
              <div>
                <label style={labelStyle}>FECHA VENCIMIENTO</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={e => setDueDate(e.target.value)}
                  style={{ ...inputStyle, colorScheme: 'dark' }}
                />
              </div>
            </div>

            {/* Lines */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>LÍNEAS DE FACTURA *</label>
                <button
                  type="button"
                  onClick={addLine}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '5px 12px', borderRadius: 8,
                    border: `1px solid rgba(249,115,22,0.35)`,
                    background: 'rgba(249,115,22,0.1)',
                    color: C.orange, fontSize: 12, cursor: 'pointer',
                    fontFamily: 'DM Sans, sans-serif',
                  }}
                >
                  <Plus size={12} />
                  Añadir línea
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* Column headers */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 70px 90px 70px 80px 32px', gap: 6 }}>
                  {['Descripción', 'Cant.', 'P. unit.', 'IVA %', 'Subtotal', ''].map(h => (
                    <div key={h} style={{ fontSize: 9, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em', padding: '0 4px' }}>{h}</div>
                  ))}
                </div>

                {lines.map((line, idx) => {
                  const sub = line.quantity * line.unitPrice;
                  return (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 70px 90px 70px 80px 32px', gap: 6, alignItems: 'center' }}>
                      <input
                        type="text"
                        value={line.description}
                        onChange={e => updateLine(idx, 'description', e.target.value)}
                        placeholder="Descripción del servicio..."
                        style={{ ...inputStyle, padding: '7px 10px', fontSize: 12 }}
                      />
                      <input
                        type="number"
                        min={0}
                        step="any"
                        value={line.quantity}
                        onChange={e => updateLine(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        style={{ ...inputStyle, padding: '7px 8px', fontSize: 12, textAlign: 'right' }}
                      />
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={line.unitPrice}
                        onChange={e => updateLine(idx, 'unitPrice', parseFloat(e.target.value) || 0)}
                        style={{ ...inputStyle, padding: '7px 8px', fontSize: 12, textAlign: 'right' }}
                      />
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step="1"
                        value={line.tax}
                        onChange={e => updateLine(idx, 'tax', parseFloat(e.target.value) || 0)}
                        style={{ ...inputStyle, padding: '7px 8px', fontSize: 12, textAlign: 'right' }}
                      />
                      <div style={{
                        padding: '7px 8px', fontSize: 12,
                        color: C.orange, fontFamily: 'JetBrains Mono, monospace',
                        textAlign: 'right',
                        background: C.bg, border: `1px solid ${C.border}`,
                        borderRadius: 8,
                      }}>
                        {formatEuro(sub)}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        disabled={lines.length === 1}
                        style={{
                          width: 32, height: 32, borderRadius: 8,
                          border: `1px solid ${C.border}`,
                          background: 'transparent', cursor: lines.length === 1 ? 'not-allowed' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: lines.length === 1 ? C.textMuted : C.error,
                          opacity: lines.length === 1 ? 0.4 : 1,
                        }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Totals preview */}
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: C.textSecondary }}>Base imponible</span>
                <span style={{ fontSize: 12, color: C.textPrimary, fontFamily: 'JetBrains Mono, monospace' }}>{formatEuro(subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 12, color: C.textSecondary }}>IVA</span>
                <span style={{ fontSize: 12, color: C.textPrimary, fontFamily: 'JetBrains Mono, monospace' }}>{formatEuro(vatAmount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#fff', fontFamily: 'Syne, sans-serif' }}>TOTAL</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: C.orange, fontFamily: 'Syne, sans-serif' }}>{formatEuro(total)}</span>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label style={labelStyle}>NOTAS</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Condiciones de pago, referencias, etc."
                rows={3}
                style={{
                  ...inputStyle,
                  resize: 'vertical',
                  lineHeight: 1.5,
                }}
              />
            </div>

            {/* Error — ERP-52: incluye botón de reintento para errores de concurrencia */}
            {error && (
              <div style={{
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
                borderRadius: 8, padding: '10px 14px',
                fontSize: 13, color: C.error,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{error}</span>
                {pendingPayload && (
                  <button
                    type="button"
                    onClick={() => { setError(''); mutation.mutate(pendingPayload); }}
                    style={{
                      padding: '4px 10px', borderRadius: 6, border: `1px solid rgba(239,68,68,0.4)`,
                      background: 'rgba(239,68,68,0.15)', color: C.error,
                      fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      fontFamily: 'Syne, sans-serif', whiteSpace: 'nowrap',
                    }}
                  >
                    Reintentar
                  </button>
                )}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
              <button
                type="button"
                onClick={onClose}
                disabled={mutation.isPending}
                style={{
                  padding: '10px 20px', borderRadius: 10,
                  border: `1px solid ${C.border}`,
                  background: 'transparent',
                  color: C.textSecondary, fontSize: 13, cursor: 'pointer',
                  fontFamily: 'DM Sans, sans-serif',
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={mutation.isPending}
                style={{
                  padding: '10px 24px', borderRadius: 10,
                  border: `1px solid rgba(249,115,22,0.4)`,
                  background: mutation.isPending ? 'rgba(249,115,22,0.3)' : C.orange,
                  color: '#fff', fontSize: 13, fontWeight: 700, cursor: mutation.isPending ? 'not-allowed' : 'pointer',
                  fontFamily: 'Syne, sans-serif',
                  display: 'flex', alignItems: 'center', gap: 8,
                  transition: 'background 0.15s',
                }}
              >
                {mutation.isPending ? (
                  <>
                    <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                    Creando...
                  </>
                ) : (
                  <>
                    <Plus size={14} />
                    Crear Factura
                  </>
                )}
              </button>
            </div>

          </div>
        </form>
      </div>
    </>
  );
}

// ─── Invoice Detail Panel ─────────────────────────────────────────────────────

function InvoiceDetailPanel({ invoice, onClose, isMobile, onStatusChanged }: { invoice: Invoice; onClose: () => void; isMobile: boolean; onStatusChanged: (msg: string) => void }) {
  const queryClient = useQueryClient();
  const [editMode, setEditMode] = useState(false);
  const [editLines, setEditLines] = useState<LineItem[]>([]);
  const [editError, setEditError] = useState('');
  const [statusError, setStatusError] = useState('');

  function enterEditMode() {
    setEditLines(invoice.items.map(it => ({
      description: it.description,
      quantity: it.quantity,
      unitPrice: it.unitPrice,
      tax: it.tax,
    })));
    setEditError('');
    setEditMode(true);
  }

  function cancelEdit() {
    setEditMode(false);
    setEditError('');
  }

  function updateEditLine(idx: number, field: keyof LineItem, value: string | number) {
    setEditLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  }

  function addEditLine() {
    setEditLines(prev => [...prev, emptyLine()]);
  }

  function removeEditLine(idx: number) {
    setEditLines(prev => prev.filter((_, i) => i !== idx));
  }

  const editSubtotals = editLines.map(l => l.quantity * l.unitPrice);
  const editSubtotal = editSubtotals.reduce((s, v) => s + v, 0);
  const editVatAmount = editLines.reduce((s, l, i) => s + editSubtotals[i] * (l.tax / 100), 0);
  const editTotal = editSubtotal + editVatAmount;

  const saveMutation = useMutation({
    mutationFn: (data: any) => invoicesApi.update(invoice.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setEditMode(false);
      setEditError('');
    },
    onError: () => setEditError('Error al guardar los cambios. Inténtalo de nuevo.'),
  });

  const statusMutation = useMutation({
    mutationFn: (newStatus: InvoiceStatus) => invoicesApi.changeStatus(invoice.id, newStatus),
    onSuccess: (_data, newStatus) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setStatusError('');
      const msgs: Record<string, string> = {
        sent: `Factura ${invoice.number} marcada como enviada`,
        paid: `Factura ${invoice.number} marcada como pagada ✓`,
        cancelled: `Factura ${invoice.number} cancelada`,
      };
      onStatusChanged(msgs[newStatus] ?? `Factura ${invoice.number} actualizada`);
    },
    onError: () => setStatusError('Error al cambiar el estado. Inténtalo de nuevo.'),
  });

  function handleSave() {
    if (editLines.length === 0) { setEditError('Debe haber al menos una línea.'); return; }
    if (editLines.some(l => !l.description.trim())) { setEditError('Todas las líneas deben tener descripción.'); return; }
    setEditError('');
    saveMutation.mutate({
      items: editLines.map(l => ({
        description: l.description,
        quantity: Number(l.quantity),
        unitPrice: Number(l.unitPrice),
        tax: Number(l.tax),
      })),
    });
  }

  const editInputStyle: React.CSSProperties = {
    background: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    color: C.textPrimary,
    fontSize: 12,
    padding: '7px 8px',
    outline: 'none',
    fontFamily: 'DM Sans, sans-serif',
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 200, backdropFilter: 'blur(2px)' }}
      />
      <div style={{
        position: 'fixed',
        top: isMobile ? 0 : '50%',
        left: isMobile ? 0 : '50%',
        transform: isMobile ? 'none' : 'translate(-50%, -50%)',
        zIndex: 201,
        width: isMobile ? '100%' : '92%',
        maxWidth: isMobile ? 'none' : 680,
        height: isMobile ? '100%' : 'auto',
        maxHeight: isMobile ? '100%' : '88vh',
        overflowY: 'auto',
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: isMobile ? 0 : 16,
        boxShadow: '0 24px 72px rgba(0,0,0,0.65)',
      }}>

        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 10,
            background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <FileText size={18} color={C.orange} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 18, color: '#fff', margin: 0, letterSpacing: '-0.02em' }}>
              {invoice.number}
            </h2>
            <p style={{ fontSize: 12, color: C.textSecondary, margin: 0 }}>
              {invoice.client?.name}{invoice.client?.company ? ` · ${invoice.client.company}` : ''}
            </p>
          </div>
          <StatusBadge status={invoice.status} />
          <button
            onClick={onClose}
            style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textSecondary, marginLeft: 8 }}
          >
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 24 }}>

          {/* Dates row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 16px' }}>
              <div style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', marginBottom: 4 }}>EMISIÓN</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Calendar size={12} color={C.textSecondary} />
                <span style={{ fontSize: 13, color: C.textPrimary }}>{formatDate(invoice.issueDate)}</span>
              </div>
            </div>
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 16px' }}>
              <div style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', marginBottom: 4 }}>VENCIMIENTO</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Calendar size={12} color={C.textSecondary} />
                <span style={{ fontSize: 13, color: invoice.dueDate ? C.textPrimary : C.textMuted }}>
                  {invoice.dueDate ? formatDate(invoice.dueDate) : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Items section */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em' }}>LÍNEAS DE FACTURA</div>
              {invoice.status === 'draft' && !editMode && (
                <button
                  onClick={enterEditMode}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '5px 12px', borderRadius: 8,
                    border: `1px solid rgba(249,115,22,0.35)`,
                    background: 'rgba(249,115,22,0.1)',
                    color: C.orange, fontSize: 12, cursor: 'pointer',
                    fontFamily: 'DM Sans, sans-serif',
                  }}
                >
                  <Plus size={12} />
                  Editar líneas
                </button>
              )}
            </div>

            {editMode ? (
              /* Edit mode: inline line editor */
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* Column headers */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 70px 90px 70px 80px 32px', gap: 6 }}>
                  {['Descripción', 'Cant.', 'P. unit.', 'IVA %', 'Subtotal', ''].map(h => (
                    <div key={h} style={{ fontSize: 9, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em', padding: '0 4px' }}>{h}</div>
                  ))}
                </div>

                {editLines.map((line, idx) => {
                  const sub = line.quantity * line.unitPrice;
                  return (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 70px 90px 70px 80px 32px', gap: 6, alignItems: 'center' }}>
                      <input
                        type="text"
                        value={line.description}
                        onChange={e => updateEditLine(idx, 'description', e.target.value)}
                        placeholder="Descripción del servicio..."
                        style={editInputStyle}
                      />
                      <input
                        type="number"
                        min={0}
                        step="any"
                        value={line.quantity}
                        onChange={e => updateEditLine(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        style={{ ...editInputStyle, textAlign: 'right' }}
                      />
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={line.unitPrice}
                        onChange={e => updateEditLine(idx, 'unitPrice', parseFloat(e.target.value) || 0)}
                        style={{ ...editInputStyle, textAlign: 'right' }}
                      />
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step="1"
                        value={line.tax}
                        onChange={e => updateEditLine(idx, 'tax', parseFloat(e.target.value) || 0)}
                        style={{ ...editInputStyle, textAlign: 'right' }}
                      />
                      <div style={{
                        padding: '7px 8px', fontSize: 12,
                        color: C.orange, fontFamily: 'JetBrains Mono, monospace',
                        textAlign: 'right',
                        background: C.bg, border: `1px solid ${C.border}`,
                        borderRadius: 8,
                      }}>
                        {formatEuro(sub)}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeEditLine(idx)}
                        disabled={editLines.length === 1}
                        style={{
                          width: 32, height: 32, borderRadius: 8,
                          border: `1px solid ${C.border}`,
                          background: 'transparent', cursor: editLines.length === 1 ? 'not-allowed' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: editLines.length === 1 ? C.textMuted : C.error,
                          opacity: editLines.length === 1 ? 0.4 : 1,
                        }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={addEditLine}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '6px 12px', borderRadius: 8,
                    border: `1px dashed ${C.border}`,
                    background: 'transparent',
                    color: C.textSecondary, fontSize: 12, cursor: 'pointer',
                    fontFamily: 'DM Sans, sans-serif',
                    alignSelf: 'flex-start',
                    marginTop: 2,
                  }}
                >
                  <Plus size={12} />
                  Añadir línea
                </button>

                {/* Live totals in edit mode */}
                <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 16px', marginTop: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: C.textSecondary }}>Base imponible</span>
                    <span style={{ fontSize: 12, color: C.textPrimary, fontFamily: 'JetBrains Mono, monospace' }}>{formatEuro(editSubtotal)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${C.border}` }}>
                    <span style={{ fontSize: 12, color: C.textSecondary }}>IVA</span>
                    <span style={{ fontSize: 12, color: C.textPrimary, fontFamily: 'JetBrains Mono, monospace' }}>{formatEuro(editVatAmount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#fff', fontFamily: 'Syne, sans-serif' }}>TOTAL</span>
                    <span style={{ fontSize: 16, fontWeight: 800, color: C.orange, fontFamily: 'Syne, sans-serif' }}>{formatEuro(editTotal)}</span>
                  </div>
                </div>

                {/* Edit error */}
                {editError && (
                  <div style={{
                    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
                    borderRadius: 8, padding: '10px 14px',
                    fontSize: 13, color: C.error,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <AlertCircle size={14} />
                    {editError}
                  </div>
                )}

                {/* Save / Cancel */}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    disabled={saveMutation.isPending}
                    style={{
                      padding: '9px 18px', borderRadius: 10,
                      border: `1px solid ${C.border}`,
                      background: 'transparent',
                      color: C.textSecondary, fontSize: 13, cursor: 'pointer',
                      fontFamily: 'DM Sans, sans-serif',
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saveMutation.isPending}
                    style={{
                      padding: '9px 22px', borderRadius: 10,
                      border: `1px solid rgba(249,115,22,0.4)`,
                      background: saveMutation.isPending ? 'rgba(249,115,22,0.3)' : C.orange,
                      color: '#fff', fontSize: 13, fontWeight: 700,
                      cursor: saveMutation.isPending ? 'not-allowed' : 'pointer',
                      fontFamily: 'Syne, sans-serif',
                      display: 'flex', alignItems: 'center', gap: 8,
                      transition: 'background 0.15s',
                    }}
                  >
                    {saveMutation.isPending ? (
                      <>
                        <div style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={14} />
                        Guardar
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              /* Read-only items table */
              <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                      {['Descripción', 'Cant.', 'P. unit.', 'IVA', 'Subtotal'].map(h => (
                        <th key={h} style={{ padding: '9px 12px', textAlign: h === 'Descripción' ? 'left' : 'right', color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', fontSize: 10, letterSpacing: '0.05em', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map((item, i) => (
                      <tr key={item.id ?? i} style={{ borderBottom: i < invoice.items.length - 1 ? `1px solid rgba(30,30,46,0.6)` : 'none' }}>
                        <td style={{ padding: '9px 12px', color: C.textPrimary }}>{item.description}</td>
                        <td style={{ padding: '9px 12px', color: C.textSecondary, textAlign: 'right' }}>{item.quantity}</td>
                        <td style={{ padding: '9px 12px', color: C.textSecondary, textAlign: 'right' }}>{formatEuro(item.unitPrice)}</td>
                        <td style={{ padding: '9px 12px', color: C.textSecondary, textAlign: 'right' }}>{item.tax}%</td>
                        <td style={{ padding: '9px 12px', color: C.textPrimary, textAlign: 'right', fontWeight: 600 }}>{formatEuro(item.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Totals (read-only mode only) */}
          {!editMode && (
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: C.textSecondary }}>Base imponible</span>
                <span style={{ fontSize: 13, color: C.textPrimary }}>{formatEuro(invoice.subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 13, color: C.textSecondary }}>IVA ({invoice.subtotal > 0 ? Math.round((invoice.vatAmount / invoice.subtotal) * 100) : 0}%)</span>
                <span style={{ fontSize: 13, color: C.textPrimary }}>{formatEuro(invoice.vatAmount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#fff', fontFamily: 'Syne, sans-serif' }}>TOTAL</span>
                <span style={{ fontSize: 15, fontWeight: 800, color: C.orange, fontFamily: 'Syne, sans-serif' }}>{formatEuro(invoice.total)}</span>
              </div>
            </div>
          )}

          {/* Notes */}
          {invoice.notes && (
            <div style={{ background: 'rgba(249,115,22,0.05)', border: '1px solid rgba(249,115,22,0.15)', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
              <div style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', marginBottom: 6 }}>NOTAS</div>
              <p style={{ fontSize: 13, color: C.textSecondary, margin: 0, lineHeight: 1.5 }}>{invoice.notes}</p>
            </div>
          )}

          {/* PDF / Email actions */}
          {!editMode && (
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20 }}>
              <div style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', marginBottom: 12 }}>ACCIONES</div>

              {/* Status change buttons */}
              {(invoice.status === 'draft' || invoice.status === 'sent') && (
                <div style={{ marginBottom: 16 }}>
                  {invoice.status === 'draft' && (
                    <button
                      disabled={statusMutation.isPending}
                      onClick={() => statusMutation.mutate('sent')}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        padding: '10px 16px', borderRadius: 10,
                        border: `1px solid rgba(59,130,246,0.4)`,
                        background: 'rgba(59,130,246,0.12)',
                        color: C.info, fontSize: 13, fontWeight: 700,
                        cursor: statusMutation.isPending ? 'not-allowed' : 'pointer',
                        fontFamily: 'Syne, sans-serif', transition: 'background 0.15s',
                        opacity: statusMutation.isPending ? 0.6 : 1,
                      }}
                      onMouseEnter={e => { if (!statusMutation.isPending) e.currentTarget.style.background = 'rgba(59,130,246,0.22)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.12)'; }}
                    >
                      <Send size={14} />
                      {statusMutation.isPending ? 'Enviando…' : 'Enviar factura'}
                    </button>
                  )}

                  {invoice.status === 'sent' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <button
                        disabled={statusMutation.isPending}
                        onClick={() => statusMutation.mutate('paid')}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                          padding: '10px 16px', borderRadius: 10,
                          border: `1px solid rgba(34,197,94,0.4)`,
                          background: 'rgba(34,197,94,0.12)',
                          color: C.success, fontSize: 13, fontWeight: 700,
                          cursor: statusMutation.isPending ? 'not-allowed' : 'pointer',
                          fontFamily: 'Syne, sans-serif', transition: 'background 0.15s',
                          opacity: statusMutation.isPending ? 0.6 : 1,
                        }}
                        onMouseEnter={e => { if (!statusMutation.isPending) e.currentTarget.style.background = 'rgba(34,197,94,0.22)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(34,197,94,0.12)'; }}
                      >
                        <CheckCircle2 size={14} />
                        {statusMutation.isPending ? 'Procesando…' : 'Marcar como pagada'}
                      </button>
                      <button
                        disabled={statusMutation.isPending}
                        onClick={() => statusMutation.mutate('cancelled')}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                          padding: '10px 16px', borderRadius: 10,
                          border: `1px solid rgba(239,68,68,0.35)`,
                          background: 'rgba(239,68,68,0.08)',
                          color: C.error, fontSize: 13, fontWeight: 700,
                          cursor: statusMutation.isPending ? 'not-allowed' : 'pointer',
                          fontFamily: 'Syne, sans-serif', transition: 'background 0.15s',
                          opacity: statusMutation.isPending ? 0.6 : 1,
                        }}
                        onMouseEnter={e => { if (!statusMutation.isPending) e.currentTarget.style.background = 'rgba(239,68,68,0.18)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
                      >
                        <XCircle size={14} />
                        {statusMutation.isPending ? 'Procesando…' : 'Cancelar factura'}
                      </button>
                    </div>
                  )}

                  {statusError && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: `1px solid rgba(239,68,68,0.2)` }}>
                      <AlertCircle size={13} color={C.error} />
                      <span style={{ fontSize: 12, color: C.error }}>{statusError}</span>
                    </div>
                  )}
                </div>
              )}

              <InvoicePdfActions invoice={invoice} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Invoice Row ──────────────────────────────────────────────────────────────

function InvoiceRow({ invoice, onClick }: { invoice: Invoice; onClick: () => void }) {
  return (
    <tr
      onClick={onClick}
      style={{ cursor: 'pointer', transition: 'background 0.1s' }}
      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      <td style={{ padding: '13px 16px', borderBottom: `1px solid #1a1a28` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FileText size={13} color={C.orange} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary, fontFamily: 'JetBrains Mono, monospace' }}>{invoice.number}</div>
            {invoice.project && <div style={{ fontSize: 11, color: C.textMuted }}>{invoice.project.reference}</div>}
          </div>
        </div>
      </td>
      <td style={{ padding: '13px 16px', borderBottom: `1px solid #1a1a28` }}>
        <div style={{ fontSize: 13, color: C.textPrimary }}>{invoice.client?.name ?? '—'}</div>
        {invoice.client?.company && <div style={{ fontSize: 11, color: C.textSecondary }}>{invoice.client.company}</div>}
      </td>
      <td style={{ padding: '13px 16px', borderBottom: `1px solid #1a1a28` }}>
        <span style={{ fontSize: 12, color: C.textSecondary, fontFamily: 'JetBrains Mono, monospace' }}>
          {formatDate(invoice.issueDate)}
        </span>
      </td>
      <td style={{ padding: '13px 16px', borderBottom: `1px solid #1a1a28` }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.orange, fontFamily: 'JetBrains Mono, monospace' }}>
          {formatEuro(invoice.total)}
        </span>
      </td>
      <td style={{ padding: '13px 16px', borderBottom: `1px solid #1a1a28` }}>
        <StatusBadge status={invoice.status} />
      </td>
      <td style={{ padding: '13px 16px', borderBottom: `1px solid #1a1a28` }}
          onClick={e => e.stopPropagation()}>
        <InvoicePdfActions invoice={invoice} compact />
      </td>
      <td style={{ padding: '13px 12px', borderBottom: `1px solid #1a1a28`, textAlign: 'right' }}>
        <ChevronRight size={14} color={C.textMuted} />
      </td>
    </tr>
  );
}

// ─── Stats Card ───────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color: string }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 20px', flex: '1 1 160px', minWidth: 0 }}>
      <div style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color, fontFamily: 'Syne, sans-serif', letterSpacing: '-0.03em' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.textSecondary, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ─── Main InvoicesPage ────────────────────────────────────────────────────────

export default function InvoicesPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | InvoiceStatus>('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showNewModal, setShowNewModal] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    if (!toastMessage) return;
    const timer = setTimeout(() => setToastMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  const { data: apiInvoices, isLoading } = useQuery({
    queryKey: ['invoices', statusFilter],
    queryFn: () => invoicesApi.list(statusFilter ? { status: statusFilter } : undefined),
    retry: false,
  });

  // ERP-78: usar apiInvoices.data (objeto { data, total }) en lugar de apiInvoices directamente
  const invoices: Invoice[] = apiInvoices?.data ?? [];

  // ERP-77: derive selected from live query data so it auto-syncs after status/edit mutations
  const selected = useMemo(
    () => (selectedId !== null ? (invoices.find(inv => inv.id === selectedId) ?? null) : null),
    [invoices, selectedId],
  );

  const filtered = useMemo(() => {
    let list = invoices;
    if (statusFilter) list = list.filter(i => i.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        i.number.toLowerCase().includes(q) ||
        (i.client?.name || '').toLowerCase().includes(q) ||
        (i.client?.company || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [invoices, statusFilter, search]);

  // Stats
  const totalPaid   = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.total, 0);
  const totalPending = invoices.filter(i => i.status === 'sent').reduce((s, i) => s + i.total, 0);
  const countDraft  = invoices.filter(i => i.status === 'draft').length;

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px', minHeight: '100vh', background: C.bg, fontFamily: 'DM Sans, sans-serif' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, rgba(249,115,22,0.2), rgba(249,115,22,0.05))', border: '1px solid rgba(249,115,22,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DollarSign size={18} color={C.orange} />
            </div>
            <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 900, fontSize: isMobile ? 24 : 28, color: '#fff', margin: 0, letterSpacing: '-0.03em' }}>
              Facturas
            </h1>
          </div>
          <p style={{ fontSize: 13, color: C.textSecondary, margin: 0 }}>
            {filtered.length} factura{filtered.length !== 1 ? 's' : ''}
            {statusFilter ? ` · ${STATUS_CONFIG[statusFilter].label.toLowerCase()}` : ''}
          </p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: isMobile ? '10px 16px' : '10px 20px',
            borderRadius: 10,
            border: '1px solid rgba(249,115,22,0.4)',
            background: C.orange,
            color: '#fff', fontSize: 13, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'Syne, sans-serif',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = C.orangeHover)}
          onMouseLeave={e => (e.currentTarget.style.background = C.orange)}
        >
          <Plus size={15} />
          {!isMobile && 'Nueva Factura'}
        </button>
      </div>

      {/* Success message */}
      {successMsg && (
        <div style={{
          background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)',
          borderRadius: 10, padding: '12px 18px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 10,
          fontSize: 13, color: C.success,
        }}>
          <CheckCircle2 size={15} />
          {successMsg}
          <button
            onClick={() => setSuccessMsg('')}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: C.success, display: 'flex', padding: 0 }}
          >
            <X size={13} />
          </button>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <StatCard label="COBRADO" value={formatEuro(totalPaid)} sub={`${invoices.filter(i => i.status === 'paid').length} facturas`} color={C.success} />
        <StatCard label="PENDIENTE DE COBRO" value={formatEuro(totalPending)} sub={`${invoices.filter(i => i.status === 'sent').length} enviadas`} color={C.warning} />
        <StatCard label="BORRADORES" value={String(countDraft)} sub="sin enviar" color={C.textSecondary} />
        <StatCard label="TOTAL FACTURADO" value={formatEuro(invoices.reduce((s, i) => s + i.total, 0))} sub="todas las facturas" color={C.orange} />
      </div>

      {/* Filters bar */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 200px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px' }}>
          <Search size={14} color={C.textSecondary} />
          <input
            type="text"
            placeholder="Buscar por número, cliente..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontSize: 13, width: '100%', fontFamily: 'DM Sans, sans-serif' }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', padding: 0 }}>
              <X size={13} />
            </button>
          )}
        </div>

        {/* Status filter pills */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {STATUS_FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value as '' | InvoiceStatus)}
              style={{
                padding: '6px 14px', borderRadius: 20,
                border: `1px solid ${statusFilter === f.value ? C.orange : C.border}`,
                background: statusFilter === f.value ? 'rgba(249,115,22,0.15)' : 'transparent',
                color: statusFilter === f.value ? C.orange : C.textSecondary,
                fontSize: 12, cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
                transition: 'all 0.15s',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div style={{ width: 32, height: 32, border: `3px solid ${C.border}`, borderTopColor: C.orange, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
          {isMobile ? (
            /* Mobile: card list */
            <div>
              {filtered.map(inv => (
                <div
                  key={inv.id}
                  style={{ padding: '16px', borderBottom: `1px solid ${C.border}`, cursor: 'pointer' }}
                  onClick={() => setSelectedId(inv.id)}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary, fontFamily: 'JetBrains Mono, monospace' }}>{inv.number}</span>
                    <StatusBadge status={inv.status} />
                  </div>
                  <div style={{ fontSize: 13, color: C.textSecondary, marginBottom: 6 }}>{inv.client?.name ?? '—'}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: C.orange }}>{formatEuro(inv.total)}</span>
                    <div onClick={e => e.stopPropagation()}>
                      <InvoicePdfActions invoice={inv} compact />
                    </div>
                  </div>
                </div>
              ))}
              {filtered.length === 0 && (
                <div style={{ padding: '48px 16px', textAlign: 'center' }}>
                  <AlertCircle size={32} color={C.textMuted} style={{ margin: '0 auto 12px', display: 'block' }} />
                  <p style={{ color: C.textMuted, fontSize: 14, margin: 0 }}>No se encontraron facturas</p>
                </div>
              )}
            </div>
          ) : (
            /* Desktop: table */
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {['Nº Factura', 'Cliente', 'Fecha', 'Total', 'Estado', 'Acciones', ''].map(h => (
                    <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', fontWeight: 600 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(inv => (
                  <InvoiceRow key={inv.id} invoice={inv} onClick={() => setSelectedId(inv.id)} />
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: '48px 16px', textAlign: 'center' }}>
                      <AlertCircle size={28} color={C.textMuted} style={{ margin: '0 auto 10px', display: 'block' }} />
                      <p style={{ color: C.textMuted, fontSize: 14, margin: 0 }}>No se encontraron facturas</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Detail panel */}
      {selected && (
        <InvoiceDetailPanel
          invoice={selected}
          onClose={() => setSelectedId(null)}
          isMobile={isMobile}
          onStatusChanged={(msg) => setToastMessage(msg)}
        />
      )}

      {/* New invoice modal */}
      {showNewModal && (
        <NewInvoiceModal
          isMobile={isMobile}
          onClose={() => setShowNewModal(false)}
          onSuccess={(num) => {
            setShowNewModal(false);
            setSuccessMsg(num ? `Factura ${num} creada correctamente.` : 'Factura creada correctamente.');
            setTimeout(() => setSuccessMsg(''), 6000);
          }}
        />
      )}

      {/* Status change toast */}
      {toastMessage && (
        <div style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 9999,
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 20px', borderRadius: 12,
          background: C.card,
          border: `1px solid rgba(249,115,22,0.35)`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          fontSize: 13, fontWeight: 600, color: C.textPrimary,
          fontFamily: 'DM Sans, sans-serif',
          animation: 'toastIn 0.22s ease',
          maxWidth: 360,
        }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <CheckCircle2 size={14} color={C.orange} />
          </div>
          <span style={{ flex: 1 }}>{toastMessage}</span>
          <button
            onClick={() => setToastMessage(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', padding: 0, flexShrink: 0 }}
          >
            <X size={13} />
          </button>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes toastIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
